import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const AuthContext = createContext(null);

const STORAGE_KEY = "flowtrak_auth_v1";
const MODE_KEY = "flowtrak_mode_v1";
const ACTIVE_STORE_KEY = "flowtrak_active_store_v1";
const ADMIN_BACKUP_KEY = "flowtrak_admin_backup_v1";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function loginErrorMessage(err) {
  const msg = safeString(err?.message).trim();
  if (/^invalid credentials\.?$/i.test(msg)) return "Invalid PIN or password.";
  return msg || "Login failed.";
}

function normalizeEmail(v) {
  return safeString(v).trim().toLowerCase();
}

function toPolishedName(value) {
  return safeString(value)
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) =>
      word
        .split(/([-'])/)
        .map((piece) =>
          piece === "-" || piece === "'"
            ? piece
            : piece.charAt(0).toUpperCase() + piece.slice(1)
        )
        .join("")
    )
    .join(" ");
}

function buildFullName(firstName, lastName, fallbackEmail = "") {
  const full = [safeString(firstName).trim(), safeString(lastName).trim()]
    .filter(Boolean)
    .join(" ")
    .trim();

  return full || safeString(fallbackEmail).trim() || "User";
}

function splitName(fullName = "") {
  const parts = safeString(fullName).trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return { firstName: "", lastName: "" };
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };

  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

function normalizeUserShape(input) {
  const u = input && typeof input === "object" ? input : {};

  const email = normalizeEmail(u.email);
  const firstNameRaw = safeString(u.firstName || u.first_name || "").trim();
  const lastNameRaw = safeString(u.lastName || u.last_name || "").trim();

  const displayNameRaw = safeString(
    u.displayName || u.display_name || ""
  ).trim();

  const fullNameRaw = safeString(
    u.fullName || u.full_name || u.name || displayNameRaw || ""
  ).trim();

  const split = splitName(fullNameRaw);

  const firstName = toPolishedName(firstNameRaw || split.firstName || "");
  const lastName = toPolishedName(lastNameRaw || split.lastName || "");
  const name = buildFullName(firstName, lastName, email);

  return {
    ...u,
    id: safeString(u.id || u.userId || u.sub).trim(),
    userId: safeString(u.userId || u.id || u.sub).trim(),
    email,
    firstName,
    lastName,
    name,
    fullName: name,
    displayName: name,
    role: safeString(u.role).trim().toLowerCase(),
    storeRole: safeString(u.storeRole || u.store_role || "").trim().toLowerCase(),
    storeId: safeString(
      u.storeId || u.businessUnitId || u.business_unit_id || ""
    ).trim(),
    businessUnitId: safeString(
      u.businessUnitId || u.business_unit_id || u.storeId || ""
    ).trim(),
    storeCode: safeString(
      u.storeCode || u.businessUnitCode || u.companyCode
    ).trim(),
    storeName: safeString(u.storeName || u.store_name || "").trim(),
    groupName: safeString(u.groupName || u.group_name || "").trim(),
    timezone: safeString(u.timezone || "America/Los_Angeles").trim(),
    platformRole: normalizePlatformRole(
      u.platformRole || u.platform_role || u.platform_permission
    ),
    isImpersonating: !!(
      u.isImpersonating ||
      u.is_impersonating ||
      u.impersonationSessionId ||
      u.impersonation_session_id
    ),
    impersonatorUserId: safeString(
      u.impersonatorUserId || u.impersonator_user_id || ""
    ).trim(),
    impersonationSessionId: safeString(
      u.impersonationSessionId || u.impersonation_session_id || ""
    ).trim(),
  };
}

function base64UrlDecode(input) {
  const s = safeString(input).replace(/-/g, "+").replace(/_/g, "/");
  const pad = s.length % 4 === 0 ? "" : "=".repeat(4 - (s.length % 4));
  try {
    const binary = window.atob(s + pad);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return "";
  }
}

function decodeJwt(token) {
  const t = safeString(token).trim();
  if (!t || !t.includes(".")) return null;

  const parts = t.split(".");
  if (parts.length < 2) return null;

  const payloadRaw = base64UrlDecode(parts[1]);
  if (!payloadRaw) return null;

  try {
    return JSON.parse(payloadRaw);
  } catch {
    return null;
  }
}

function nowSec() {
  return Math.floor(Date.now() / 1000);
}

function isTokenExpired(payload) {
  const exp = Number(payload?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return false;
  return exp <= nowSec();
}

function pickUserFromPayload(p) {
  return normalizeUserShape(p);
}

function normalizeStoreRow(row) {
  const r = row && typeof row === "object" ? row : {};
  return {
    employmentId: safeString(r.employmentId || r.employment_id).trim(),
    storeId: safeString(r.storeId || r.store_id).trim(),
    storeName: safeString(r.storeName || r.store_name).trim(),
    storeCode: safeString(r.storeCode || r.store_code).trim(),
    groupName: safeString(r.groupName || r.group_name).trim(),
    role: safeString(r.role).trim().toLowerCase(),
    status: safeString(r.status).trim().toUpperCase() || "ACTIVE",
    isActive:
      typeof r.isActive === "boolean"
        ? r.isActive
        : typeof r.is_active === "boolean"
        ? r.is_active
        : true,
  };
}

function normalizeStores(rows) {
  return Array.isArray(rows) ? rows.map(normalizeStoreRow) : [];
}

function pickMembershipForStore(stores, activeStoreId, user) {
  const list = normalizeStores(stores).filter(
    (s) => safeString(s.storeId).trim() && s.isActive !== false
  );

  const wantedStoreId = safeString(activeStoreId || "").trim();

  if (wantedStoreId) {
    const match = list.find((s) => safeString(s.storeId).trim() === wantedStoreId);
    if (match) return match;
  }

  const userStoreId = safeString(user?.storeId || user?.businessUnitId).trim();
  if (userStoreId) {
    const userMatch = list.find((s) => safeString(s.storeId).trim() === userStoreId);
    if (userMatch) return userMatch;
  }

  if (list.length) return list[0];

  const fallbackStoreId = safeString(user?.storeId || user?.businessUnitId).trim();
  const fallbackStoreCode = safeString(user?.storeCode).trim();
  const fallbackRole = safeString(user?.storeRole || user?.role).trim().toLowerCase();

  if (fallbackStoreId || fallbackStoreCode || fallbackRole) {
    return {
      employmentId: "",
      storeId: fallbackStoreId,
      storeName: safeString(user?.storeName).trim(),
      storeCode: fallbackStoreCode,
      groupName: safeString(user?.groupName).trim(),
      role: fallbackRole,
      status: "ACTIVE",
      isActive: true,
    };
  }

  return null;
}

function getResolvedStoreId(stores, activeStoreId, membership, user) {
  const list = normalizeStores(stores).filter((s) => safeString(s.storeId).trim());

  const active = safeString(activeStoreId).trim();
  if (active && list.some((s) => safeString(s.storeId).trim() === active)) {
    return active;
  }

  const membershipStoreId = safeString(membership?.storeId).trim();
  if (
    membershipStoreId &&
    (list.length === 0 ||
      list.some((s) => safeString(s.storeId).trim() === membershipStoreId))
  ) {
    return membershipStoreId;
  }

  const userStoreId = safeString(user?.storeId || user?.businessUnitId).trim();
  if (
    userStoreId &&
    (list.length === 0 ||
      list.some((s) => safeString(s.storeId).trim() === userStoreId))
  ) {
    return userStoreId;
  }

  return safeString(list?.[0]?.storeId).trim();
}

async function readResponseBody(res) {
  const contentType = safeString(res.headers?.get?.("content-type") || "");
  if (res.status === 204) return {};

  if (contentType.includes("application/json")) {
    return await res.json().catch(() => ({}));
  }

  const text = await res.text().catch(() => "");
  if (!text) return {};

  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function clearStoredSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(MODE_KEY);
    localStorage.removeItem(ACTIVE_STORE_KEY);
    localStorage.removeItem(ADMIN_BACKUP_KEY);
  } catch {}
}

function hasAdminBackup() {
  try {
    return !!safeString(localStorage.getItem(ADMIN_BACKUP_KEY)).trim();
  } catch {
    return false;
  }
}

function getInitialAuthState() {
  const fallback = {
    token: "",
    user: null,
    stores: [],
    membership: null,
    hydrated: true,
    mode: "store",
    activeStoreId: "",
  };

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const explicitMode = safeString(localStorage.getItem(MODE_KEY))
      .toLowerCase()
      .trim();
    const storedActiveStoreId = safeString(
      localStorage.getItem(ACTIVE_STORE_KEY)
    ).trim();

    if (!raw) {
      return {
        ...fallback,
        mode: explicitMode === "platform" ? "platform" : "store",
        activeStoreId: storedActiveStoreId,
      };
    }

    const parsed = JSON.parse(raw);
    const t = safeString(parsed?.token).trim();

    if (!t) {
      clearStoredSession();
      return fallback;
    }

    const payload = decodeJwt(t);
    if (payload && isTokenExpired(payload)) {
      clearStoredSession();
      return fallback;
    }

    const hydratedUser = payload
      ? pickUserFromPayload(payload)
      : normalizeUserShape(parsed?.user || null);

    const hydratedStores = normalizeStores(parsed?.stores);
    const derivedMembership =
      parsed?.membership && typeof parsed.membership === "object"
        ? normalizeStoreRow(parsed.membership)
        : pickMembershipForStore(hydratedStores, storedActiveStoreId, hydratedUser);

    const resolvedStoreId = getResolvedStoreId(
      hydratedStores,
      storedActiveStoreId,
      derivedMembership,
      hydratedUser
    );

    const finalMembership = pickMembershipForStore(
      hydratedStores,
      resolvedStoreId,
      hydratedUser
    );

    const derivedMode = normalizePlatformRole(hydratedUser?.platformRole)
      ? "platform"
      : "store";

    const nextMode =
      explicitMode === "platform" || explicitMode === "store"
        ? explicitMode
        : derivedMode;

    return {
      token: t,
      user: hydratedUser,
      stores: hydratedStores,
      membership: finalMembership,
      hydrated: false,
      mode: hydratedUser?.isImpersonating ? "store" : nextMode,
      activeStoreId: resolvedStoreId,
    };
  } catch {
    clearStoredSession();
    return fallback;
  }
}

export function AuthProvider({ children }) {
  const initial = useMemo(() => getInitialAuthState(), []);

  const [token, setToken] = useState(initial.token);
  const [user, setUser] = useState(initial.user);
  const [stores, setStores] = useState(initial.stores || []);
  const [membership, setMembership] = useState(initial.membership || null);
  const [hydrated, setHydrated] = useState(initial.hydrated);
  const [mode, setMode] = useState(initial.mode);
  const [activeStoreId, setActiveStoreIdState] = useState(initial.activeStoreId);
  const [hasImpersonationBackup, setHasImpersonationBackup] = useState(() =>
    hasAdminBackup()
  );

  const apiBase = useMemo(() => {
    const env = safeString(process.env.REACT_APP_API_BASE_URL).trim();
    const base =
      env ||
      (typeof window !== "undefined"
        ? window.location.origin.replace(/\/+$/, "")
        : "");
    console.log("[Flowtrak AuthContext] API base =", base);
    return base.replace(/\/+$/, "");
  }, []);

  const apiUrl = useCallback(
    (path = "") => {
      const p = safeString(path);
      if (!p) return apiBase || "";
      if (p.startsWith("http://") || p.startsWith("https://")) return p;
      return `${apiBase}${p.startsWith("/") ? "" : "/"}${p}`;
    },
    [apiBase]
  );

  const persistSession = useCallback((nextToken, nextUser, nextStores, nextMembership) => {
    try {
      if (!safeString(nextToken).trim()) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const normalizedUser = normalizeUserShape(nextUser);

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token: nextToken,
          user: normalizedUser,
          stores: normalizeStores(nextStores),
          membership: nextMembership ? normalizeStoreRow(nextMembership) : null,
        })
      );
    } catch {}
  }, []);

  const persistMode = useCallback((nextMode) => {
    try {
      localStorage.setItem(MODE_KEY, nextMode === "platform" ? "platform" : "store");
    } catch {}
  }, []);

  const persistActiveStore = useCallback((nextStoreId) => {
    try {
      const normalized = safeString(nextStoreId).trim();
      if (!normalized) {
        localStorage.removeItem(ACTIVE_STORE_KEY);
        return;
      }
      localStorage.setItem(ACTIVE_STORE_KEY, normalized);
    } catch {}
  }, []);

  const platformRole = useMemo(
    () => normalizePlatformRole(user?.platformRole),
    [user?.platformRole]
  );

  const isImpersonating = !!user?.isImpersonating;
  const impersonationSessionId = safeString(user?.impersonationSessionId).trim();

  const isPlatformUser = !!platformRole && !isImpersonating;
  const isPlatformMode = mode === "platform";
  const isAuthenticated = !!safeString(token).trim() && !!user;
  const isAuthed = isAuthenticated;

  const effectiveStoreId = useMemo(() => {
    return getResolvedStoreId(stores, activeStoreId, membership, user);
  }, [stores, activeStoreId, membership, user]);

  const platformToken = token;
  const platformUser = user;

  const resolveMembership = useCallback((nextStores, nextStoreId, nextUser) => {
    return pickMembershipForStore(nextStores, nextStoreId, nextUser);
  }, []);

  const signOut = useCallback(() => {
    setToken("");
    setUser(null);
    setStores([]);
    setMembership(null);
    setMode("store");
    setActiveStoreIdState("");
    setHydrated(true);
    setHasImpersonationBackup(false);
    clearStoredSession();
  }, []);

  const clearSession = signOut;

  const setSessionFromToken = useCallback(
    (nextToken, options = {}) => {
      const t = safeString(nextToken).trim();

      if (!t) {
        setToken("");
        setUser(null);
        setStores([]);
        setMembership(null);
        setHydrated(true);
        setActiveStoreIdState("");
        persistSession("", null, [], null);
        persistActiveStore("");
        return null;
      }

      const payload = decodeJwt(t);
      if (payload && isTokenExpired(payload)) {
        setToken("");
        setUser(null);
        setStores([]);
        setMembership(null);
        setHydrated(true);
        setActiveStoreIdState("");
        clearStoredSession();
        return null;
      }

      const nextUser = payload ? pickUserFromPayload(payload) : null;
      const nextStores = normalizeStores(options.stores);
      const requestedStoreId =
        safeString(options.activeStoreId).trim() ||
        safeString(nextUser?.storeId || nextUser?.businessUnitId).trim();

      const nextMembership = resolveMembership(nextStores, requestedStoreId, nextUser);
      const resolvedStoreId = getResolvedStoreId(
        nextStores,
        requestedStoreId,
        nextMembership,
        nextUser
      );
      const finalMembership = resolveMembership(nextStores, resolvedStoreId, nextUser);

      setToken(t);
      setUser(nextUser);
      setStores(nextStores);
      setMembership(finalMembership);
      setHydrated(true);
      setActiveStoreIdState(resolvedStoreId);

      persistSession(t, nextUser, nextStores, finalMembership);
      persistActiveStore(resolvedStoreId);

      return nextUser;
    },
    [persistActiveStore, persistSession, resolveMembership]
  );

  const setSession = useCallback(
    (nextToken, nextUser = null, nextStores = [], nextActiveStoreId = "") => {
      const resolvedUser =
        nextUser && typeof nextUser === "object"
          ? normalizeUserShape(nextUser)
          : undefined;

      const userFromToken = setSessionFromToken(nextToken, {
        stores: nextStores,
        activeStoreId: nextActiveStoreId,
      });

      if (resolvedUser) {
        setUser(resolvedUser);

        const nextMembership = resolveMembership(
          nextStores,
          nextActiveStoreId || resolvedUser?.storeId,
          resolvedUser
        );

        const resolvedStoreId = getResolvedStoreId(
          nextStores,
          nextActiveStoreId,
          nextMembership,
          resolvedUser
        );

        const finalMembership = resolveMembership(
          nextStores,
          resolvedStoreId,
          resolvedUser
        );

        setStores(normalizeStores(nextStores));
        setMembership(finalMembership);
        setActiveStoreIdState(resolvedStoreId);
        persistActiveStore(resolvedStoreId);
        persistSession(nextToken, resolvedUser, nextStores, finalMembership);
      }

      const finalUser = resolvedUser || userFromToken;
      const nextMode =
        finalUser?.isImpersonating || !normalizePlatformRole(finalUser?.platformRole)
          ? "store"
          : "platform";

      setMode(nextMode);
      persistMode(nextMode);

      return finalUser;
    },
    [
      persistMode,
      persistSession,
      resolveMembership,
      setSessionFromToken,
      persistActiveStore,
    ]
  );

  const apiRequest = useCallback(
    async (method, path, body, { useAuthHeader = true } = {}) => {
      const url = apiUrl(path);

      const headers = {};
      if (useAuthHeader && token) headers.Authorization = `Bearer ${token}`;
      if (useAuthHeader && effectiveStoreId) headers["X-Store-Id"] = effectiveStoreId;
      if (body != null) headers["Content-Type"] = "application/json";

      const res = await fetch(url, {
        method,
        headers,
        body: body != null ? JSON.stringify(body) : undefined,
      });

      const data = await readResponseBody(res);

      if (!res.ok) {
        if (res.status === 401 && useAuthHeader) signOut();

        const msg =
          safeString(data?.error) ||
          safeString(data?.message) ||
          `${method} failed (${res.status})`;
        throw new Error(msg);
      }

      return data;
    },
    [apiUrl, token, effectiveStoreId, signOut]
  );

  const apiGet = useCallback((path) => apiRequest("GET", path), [apiRequest]);
  const apiPost = useCallback((path, body) => apiRequest("POST", path, body), [apiRequest]);
  const apiPatch = useCallback((path, body) => apiRequest("PATCH", path, body), [apiRequest]);
  const apiDelete = useCallback((path) => apiRequest("DELETE", path), [apiRequest]);

  const publicGet = useCallback(
    (path) => apiRequest("GET", path, null, { useAuthHeader: false }),
    [apiRequest]
  );

  const publicPost = useCallback(
    (path, body) => apiRequest("POST", path, body, { useAuthHeader: false }),
    [apiRequest]
  );

  const startImpersonation = useCallback(
    async (impersonationToken) => {
      const t = safeString(impersonationToken).trim();
      if (!t) throw new Error("Missing impersonation token.");

      const payload = decodeJwt(t);
      if (!payload || isTokenExpired(payload)) {
        throw new Error("Invalid or expired impersonation token.");
      }

      const currentRaw = localStorage.getItem(STORAGE_KEY);
      if (currentRaw) {
        localStorage.setItem(ADMIN_BACKUP_KEY, currentRaw);
      }

      const nextUser = pickUserFromPayload(payload);
      const resolvedStoreId = safeString(
        nextUser?.storeId || nextUser?.businessUnitId || ""
      ).trim();

      const nextMembership = pickMembershipForStore([], resolvedStoreId, nextUser);

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          token: t,
          user: nextUser,
          stores: [],
          membership: nextMembership,
        })
      );

      localStorage.setItem(MODE_KEY, "store");
      if (resolvedStoreId) localStorage.setItem(ACTIVE_STORE_KEY, resolvedStoreId);
      else localStorage.removeItem(ACTIVE_STORE_KEY);

      setHasImpersonationBackup(true);
      const role = safeString(nextUser?.storeRole || nextUser?.role)
  .trim()
  .toLowerCase();

let landingPath = "/dashboard";

if (role === "advisor") {
  landingPath = "/advisor";
} else if (role === "parts" || role === "staff") {
  landingPath = "/parts";
} else if (role === "tech" || role === "technician") {
  landingPath = "/tech";
} else if (role === "foreman") {
  landingPath = "/shop-foreman";
} else if (role === "dispatcher") {
  landingPath = "/dispatch";
}

window.location.assign(landingPath);
    },
    []
  );

  const stopImpersonation = useCallback(async () => {
    let backupRaw = "";
    let backup = null;

    try {
      backupRaw = safeString(localStorage.getItem(ADMIN_BACKUP_KEY)).trim();
      backup = backupRaw ? JSON.parse(backupRaw) : null;
    } catch {
      backup = null;
    }

    const backupToken = safeString(backup?.token).trim();

    if (backupToken && impersonationSessionId) {
      try {
        await fetch(apiUrl("/api/platform/impersonation/stop"), {
          method: "POST",
          headers: {
            Authorization: `Bearer ${backupToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ sessionId: impersonationSessionId }),
        });
      } catch {}
    }

    if (backupRaw) {
      localStorage.setItem(STORAGE_KEY, backupRaw);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }

    localStorage.removeItem(ADMIN_BACKUP_KEY);
    localStorage.removeItem(ACTIVE_STORE_KEY);
    localStorage.setItem(MODE_KEY, "platform");

    setHasImpersonationBackup(false);
    window.location.assign("/platform/users");
  }, [apiUrl, impersonationSessionId]);

  useEffect(() => {
    let cancelled = false;

    async function validateBootSession() {
      const t = safeString(token).trim();

      if (!t) {
        if (!cancelled) setHydrated(true);
        return;
      }

      try {
        await apiRequest("GET", "/api/auth/session", null);
        if (!cancelled) setHydrated(true);
      } catch {
        if (!cancelled) setHydrated(true);
      }
    }

    validateBootSession();

    return () => {
      cancelled = true;
    };
  }, [token, apiRequest]);

  useEffect(() => {
    let cancelled = false;

    async function refreshMembershipsFromServer() {
      const t = safeString(token).trim();
      if (!t) return;

      try {
        const data = await apiRequest("GET", "/api/auth/my-stores", null);
        if (cancelled) return;

        const nextStores = normalizeStores(data?.stores);

        const preferredStoreId =
          safeString(activeStoreId).trim() ||
          safeString(membership?.storeId).trim() ||
          safeString(user?.storeId || user?.businessUnitId).trim();

        const nextMembership = resolveMembership(nextStores, preferredStoreId, user);
        const resolvedStoreId = getResolvedStoreId(
          nextStores,
          preferredStoreId,
          nextMembership,
          user
        );
        const finalMembership = resolveMembership(nextStores, resolvedStoreId, user);

        setStores(nextStores);
        setMembership(finalMembership);
        setActiveStoreIdState(resolvedStoreId);
        persistActiveStore(resolvedStoreId);
        persistSession(token, user, nextStores, finalMembership);
      } catch {}
    }

    refreshMembershipsFromServer();

    return () => {
      cancelled = true;
    };
  }, [
    token,
    apiRequest,
    activeStoreId,
    membership?.storeId,
    user,
    resolveMembership,
    persistActiveStore,
    persistSession,
  ]);

  const login = useCallback(
    async ({ pin, password } = {}) => {
      const PIN = safeString(pin).trim();
      const PW = safeString(password);

      if (!PIN) throw new Error("PIN is required.");
      if (!PW) throw new Error("Password is required.");

      try {
        const data = await apiRequest(
          "POST",
          "/api/auth/login",
          { pin: PIN, password: PW },
          { useAuthHeader: false }
        );

        const nextToken = safeString(
          data?.token || data?.accessToken || data?.jwt
        ).trim();

        if (!nextToken) throw new Error("Login succeeded but no token returned.");

        const returnedStores = normalizeStores(data?.stores);
        const returnedActiveStoreId = safeString(data?.activeStoreId).trim();

        const apiUser =
          data?.user && typeof data.user === "object"
            ? normalizeUserShape(data.user)
            : null;

        const nextUser = apiUser
          ? setSession(nextToken, apiUser, returnedStores, returnedActiveStoreId)
          : setSessionFromToken(nextToken, {
              stores: returnedStores,
              activeStoreId: returnedActiveStoreId,
            });

        const nextMembership = resolveMembership(
          returnedStores,
          returnedActiveStoreId,
          nextUser
        );
        const resolvedStoreId = getResolvedStoreId(
          returnedStores,
          returnedActiveStoreId,
          nextMembership,
          nextUser
        );
        const finalMembership = resolveMembership(
          returnedStores,
          resolvedStoreId,
          nextUser
        );

        setStores(returnedStores);
        setMembership(finalMembership);
        setActiveStoreIdState(resolvedStoreId);
        persistActiveStore(resolvedStoreId);
        persistSession(nextToken, nextUser, returnedStores, finalMembership);

        const nextPlatformRole = normalizePlatformRole(nextUser?.platformRole);
        const nextMode = nextPlatformRole ? "platform" : "store";

        setMode(nextMode);
        persistMode(nextMode);
        setHasImpersonationBackup(false);
        localStorage.removeItem(ADMIN_BACKUP_KEY);

        return data;
      } catch (e) {
        throw new Error(loginErrorMessage(e));
      }
    },
    [
      apiRequest,
      setSession,
      setSessionFromToken,
      resolveMembership,
      persistActiveStore,
      persistMode,
      persistSession,
    ]
  );

  const platformLogin = useCallback(
    async ({ email, password } = {}) => {
      const em = normalizeEmail(email);
      const pw = safeString(password);

      if (!em) throw new Error("Email is required.");
      if (!pw) throw new Error("Password is required.");

      const attempts = [
        { path: "/api/platform/login", body: { email: em, password: pw } },
        { path: "/api/auth/platform-login", body: { email: em, password: pw } },
        { path: "/api/auth/platformLogin", body: { email: em, password: pw } },
      ];

      let lastErr = null;

      for (const a of attempts) {
        try {
          const data = await apiRequest("POST", a.path, a.body, {
            useAuthHeader: false,
          });

          const nextToken = safeString(
            data?.token || data?.accessToken || data?.jwt
          ).trim();

          if (!nextToken) {
            throw new Error("Platform login succeeded but no token returned.");
          }

          const apiUser =
            data?.user && typeof data.user === "object"
              ? normalizeUserShape(data.user)
              : null;

          if (apiUser) {
            setSession(nextToken, apiUser, [], "");
          } else {
            setSessionFromToken(nextToken, { stores: [], activeStoreId: "" });
          }

          setStores([]);
          setMembership(null);
          setActiveStoreIdState("");
          persistActiveStore("");

          setMode("platform");
          persistMode("platform");
          setHasImpersonationBackup(false);
          localStorage.removeItem(ADMIN_BACKUP_KEY);

          return data;
        } catch (e) {
          lastErr = e;
        }
      }

      throw lastErr || new Error("Platform login failed.");
    },
    [apiRequest, setSession, setSessionFromToken, persistActiveStore, persistMode]
  );

  const refreshUserFromApiResponse = useCallback(
    (data = {}) => {
      const nextToken = safeString(
        data?.token || data?.accessToken || data?.jwt || token
      ).trim();

      const nextStores = normalizeStores(data?.stores || stores);
      const requestedStoreId = safeString(
        data?.activeStoreId || activeStoreId
      ).trim();

      const apiUser =
        data?.user && typeof data.user === "object"
          ? normalizeUserShape(data.user)
          : user;

      const nextMembership = resolveMembership(nextStores, requestedStoreId, apiUser);
      const resolvedStoreId = getResolvedStoreId(
        nextStores,
        requestedStoreId,
        nextMembership,
        apiUser
      );
      const finalMembership = resolveMembership(nextStores, resolvedStoreId, apiUser);

      setToken(nextToken);
      setUser(apiUser);
      setStores(nextStores);
      setMembership(finalMembership);
      setActiveStoreIdState(resolvedStoreId);
      setHydrated(true);

      persistActiveStore(resolvedStoreId);
      persistSession(nextToken, apiUser, nextStores, finalMembership);

      const nextMode =
        apiUser?.isImpersonating || !normalizePlatformRole(apiUser?.platformRole)
          ? "store"
          : "platform";

      setMode(nextMode);
      persistMode(nextMode);

      return apiUser;
    },
    [
      token,
      stores,
      activeStoreId,
      user,
      resolveMembership,
      persistActiveStore,
      persistSession,
      persistMode,
    ]
  );

  const updateProfile = useCallback(
    async ({ firstName, lastName, email } = {}) => {
      const payload = {
        firstName: toPolishedName(firstName),
        lastName: toPolishedName(lastName),
        email: normalizeEmail(email),
      };

      const data = await apiPatch("/api/auth/me", payload);
      refreshUserFromApiResponse(data);
      return data;
    },
    [apiPatch, refreshUserFromApiResponse]
  );

  const setPlatformMode = useCallback(() => {
    if (!isPlatformUser) return false;
    setMode("platform");
    persistMode("platform");
    return true;
  }, [isPlatformUser, persistMode]);

  const setStoreMode = useCallback(() => {
    const resolvedStoreId = getResolvedStoreId(stores, activeStoreId, membership, user);
    const finalMembership = resolveMembership(stores, resolvedStoreId, user);

    setMode("store");
    setMembership(finalMembership);
    setActiveStoreIdState(resolvedStoreId);
    persistMode("store");
    persistActiveStore(resolvedStoreId);
    persistSession(token, user, stores, finalMembership);
    return true;
  }, [
    activeStoreId,
    membership,
    persistActiveStore,
    persistMode,
    persistSession,
    resolveMembership,
    stores,
    token,
    user,
  ]);

  const setActiveStoreId = useCallback(
    (storeId) => {
      const requested = safeString(storeId).trim();
      const resolvedStoreId = getResolvedStoreId(stores, requested, membership, user);
      const nextMembership = resolveMembership(stores, resolvedStoreId, user);

      setActiveStoreIdState(resolvedStoreId);
      setMembership(nextMembership);
      persistActiveStore(resolvedStoreId);
      persistSession(token, user, stores, nextMembership);

      if (resolvedStoreId) {
        setMode("store");
        persistMode("store");
      }

      return resolvedStoreId;
    },
    [
      membership,
      persistActiveStore,
      persistMode,
      persistSession,
      resolveMembership,
      stores,
      token,
      user,
    ]
  );

  const clearActiveStoreId = useCallback(() => {
    const fallbackStoreId = getResolvedStoreId(stores, "", membership, user);
    const fallbackMembership = resolveMembership(stores, fallbackStoreId, user);

    setActiveStoreIdState(fallbackStoreId);
    setMembership(fallbackMembership);
    persistActiveStore(fallbackStoreId);
    persistSession(token, user, stores, fallbackMembership);
  }, [
    membership,
    persistActiveStore,
    persistSession,
    resolveMembership,
    stores,
    token,
    user,
  ]);

  const value = useMemo(
    () => ({
      token,
      user,
      stores,
      membership,
      hydrated,
      isAuthenticated,
      isAuthed,

      mode,
      setMode,
      setPlatformMode,
      setStoreMode,

      activeStoreId,
      effectiveStoreId,
      setActiveStoreId,
      clearActiveStoreId,

      platformToken,
      platformUser,
      platformRole,
      isPlatformUser,
      isPlatform: isPlatformMode,

      isImpersonating,
      impersonationSessionId,
      hasImpersonationBackup,
      startImpersonation,
      stopImpersonation,

      apiBase,
      apiUrl,
      apiGet,
      apiPost,
      apiPatch,
      apiDelete,
      publicGet,
      publicPost,

      login,
      platformLogin,
      updateProfile,
      refreshUserFromApiResponse,
      setSession,
      setSessionFromToken,
      signOut,
      clearSession,
    }),
    [
      token,
      user,
      stores,
      membership,
      hydrated,
      isAuthenticated,
      isAuthed,
      mode,
      setPlatformMode,
      setStoreMode,
      activeStoreId,
      effectiveStoreId,
      setActiveStoreId,
      clearActiveStoreId,
      platformToken,
      platformUser,
      platformRole,
      isPlatformUser,
      isPlatformMode,
      isImpersonating,
      impersonationSessionId,
      hasImpersonationBackup,
      startImpersonation,
      stopImpersonation,
      apiBase,
      apiUrl,
      apiGet,
      apiPost,
      apiPatch,
      apiDelete,
      publicGet,
      publicPost,
      login,
      platformLogin,
      updateProfile,
      refreshUserFromApiResponse,
      setSession,
      setSessionFromToken,
      signOut,
      clearSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
